import { StepType } from '@pushly/models/lib/enums/journeys'
import { orderSteps } from '@pushly/models/lib/structs/journeys'
import { isSystemStep, isBranchingStepType } from '@pushly/models/lib/structs/journeys/steps/classification'
import { getBranchingData } from '@pushly/models/lib/structs/journeys/steps/branching'
import type { IJourneyStep } from '@pushly/models/lib/structs/journeys'
import { JourneyNode } from '../types/journey-nodes'
import { STEP_CONFIG, resolveLabel } from '../step-config'
import { CatalogItemType } from '@pushly/models/lib/enums/catalogs/catalog-item-type'

type StepNameMap = Map<string, string>

const SYSTEM_STEP_NAMES: Partial<Record<StepType, string>> = {
    [StepType.TRIGGER]: 'Entry',
    [StepType.EXIT]: 'Exit',
    [StepType.PLACEHOLDER]: 'Placeholder',
    [StepType.GLOBAL_TRIGGER]: 'Entry',
}

interface BranchOwnershipEntry {
    branchLabel: string
    branchScopeKey: string
    splitTestLabel: string
}

/**
 * Builds a map of step ID → branch ownership info by walking each branch path
 * from branching nodes' out_step_ids to their join_step_ids.
 * Includes split test label to disambiguate multiple split tests with same variant names.
 */
function buildBranchOwnership(
    orderedSteps: IJourneyStep[],
    nodeMap: Map<string, JourneyNode>,
): Map<string, BranchOwnershipEntry> {
    const ownership = new Map<string, BranchOwnershipEntry>()

    // Track how many split tests we've seen of each type to generate labels
    const splitTestCounters = new Map<StepType, number>()

    for (const step of orderedSteps) {
        if (!isBranchingStepType(step.type)) continue

        const branchingData = getBranchingData(step)
        if (!branchingData) continue

        const joinStepId = branchingData.join_step_id != null ? String(branchingData.join_step_id) : null
        const branchingStepId = String(step.id)

        // Generate split test label based on type and counter
        const stepType = step.type as StepType
        const splitCount = (splitTestCounters.get(stepType) ?? 0) + 1
        splitTestCounters.set(stepType, splitCount)

        // Get the config label for this split type (e.g., "Split Test", "Condition")
        const configKey = stepType
        const stepConfig = configKey ? STEP_CONFIG[configKey] : undefined
        const splitTypeLabel = stepConfig?.label ?? String(stepType)
        const splitTestLabel = `${splitTypeLabel} #${splitCount}`

        for (const branch of branchingData.branches) {
            if (branch.out_step_id == null) continue

            const entry: BranchOwnershipEntry = {
                branchLabel: branch.label,
                branchScopeKey: `${branchingStepId}:${branch.key}`,
                splitTestLabel: splitTestLabel,
            }

            // Walk from branch out_step_id to join_step_id
            let currentId: string | null = String(branch.out_step_id)
            const visited = new Set<string>()

            while (currentId != null) {
                if (currentId === joinStepId) break
                if (visited.has(currentId)) break

                const node = nodeMap.get(currentId)
                if (!node) break

                visited.add(currentId)
                ownership.set(currentId, entry)

                // Follow out_step_id chain
                currentId = node.data.step.configuration.out_step_id != null ? String(node.data.step.configuration.out_step_id) : null
            }
        }
    }

    return ownership
}

/**
 * Generates deterministic human-readable names for all journey steps.
 *
 * System steps get fixed names (Entry, Exit, Placeholder).
 * Non-system steps get "{Label} #{N}" in global scope or
 * "{SplitTest} - {BranchLabel} > {Label} #{N}" inside a branch scope.
 * Multiple split tests are numbered to avoid ambiguity (e.g., "Split Test #1 - Third > Trending Recipe #1").
 *
 * Counters are per-scope per-type, 1-based, incremented in traversal order.
 */
export function generateStepNames(nodes: JourneyNode[], itemType?: CatalogItemType): StepNameMap {
    const nameMap: StepNameMap = new Map()

    // 1. Build node map
    const nodeMap = new Map<string, JourneyNode>()
    for (const node of nodes) {
        nodeMap.set(node.id, node)
    }

    // 2. Extract plain step objects and call orderSteps for traversal order
    const plainSteps = nodes.map((n) => n.data.step)
    const ordered = orderSteps(plainSteps)

    if (ordered.length === 0) return nameMap

    // 3. Build branch ownership map
    const branchOwnership = buildBranchOwnership(ordered, nodeMap)

    // 4. Initialize scope counters
    const globalCounters = new Map<string, number>()
    const branchCounters = new Map<string, Map<string, number>>()

    // 5. Assign names in traversal order
    for (const step of ordered) {
        const stepId = String(step.id)
        const stepType = step.type as StepType

        // System steps get fixed names
        if (isSystemStep(stepType)) {
            const fixedName = SYSTEM_STEP_NAMES[stepType]
            if (fixedName) {
                nameMap.set(stepId, fixedName)
            }
            continue
        }

        // Resolve config key: ACTION steps use configuration.type, others use step.type
        const config = step.configuration as Record<string, any> | undefined
        const configKey = stepType === StepType.ACTION ? config?.type : stepType

        // Look up label from STEP_CONFIG, fall back to step type string
        const stepConfig = configKey ? STEP_CONFIG[configKey] : undefined
        const label = stepConfig ? resolveLabel(stepConfig, itemType ?? CatalogItemType.PRODUCT) : String(stepType)

        // Check branch ownership
        const branchInfo = branchOwnership.get(stepId)

        if (branchInfo) {
            // Branch-scoped step
            const scopeKey = branchInfo.branchScopeKey
            if (!branchCounters.has(scopeKey)) {
                branchCounters.set(scopeKey, new Map())
            }
            const counters = branchCounters.get(scopeKey)!
            const count = (counters.get(label) ?? 0) + 1
            counters.set(label, count)

            // Include split test label to disambiguate multiple split tests
            nameMap.set(stepId, `${branchInfo.splitTestLabel} - ${branchInfo.branchLabel} > ${label} #${count}`)
        } else {
            // Global-scoped step
            const count = (globalCounters.get(label) ?? 0) + 1
            globalCounters.set(label, count)
            nameMap.set(stepId, `${label} #${count}`)
        }
    }

    return nameMap
}
